import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevItemComment } from '../models';
import { commentsByReadStatus } from '../module';


const KEY_ACTION = ActionType.SetDevItemCommentsReadRequest;
const ACTIONS    = [
  ActionType.SetDevItemCommentsReadRequest,
  ActionType.SetDevItemCommentsReadResponse,
  ActionType.SetDevItemCommentsReadError,
];


type Payload = DevItemComment[];


type Meta = {
  projectId: number;
  devPlanItemId: number;
};


/**
 * Dispatch this to retrieve all dev plan collaborators for the current project/participant pair
 *
 * @param projectId - the database Id for the project the dev plan for which collaborators are to be retrieved
 * @param devPlanItemId - the database Id for the dev item the comments for which we wish to update
 */
export const setDevItemCommentsReadAction = (
  projectId: number,
  devPlanItemId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SetDevItemCommentsRead,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        devPlanItemId: devPlanItemId,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId:     projectId,
        devPlanItemId: devPlanItemId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${devPlanItemId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, devPlanItemId } = action.meta;

  const metaTag = `${projectId}-${devPlanItemId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  const comments = R.path<DevItemComment[]>(
    ['devPlan', 'comments', `${projectId}`, `${devPlanItemId}`],
    successState,
  );

  const { readComments, unreadComments } = commentsByReadStatus(comments ?? []);

  const updatedUnreadComments = unreadComments?.map(c => R.assoc('commentRead', true, c));

  return R.assocPath(
    ['devPlan', 'comments', `${projectId}`, `${devPlanItemId}`],
    readComments.concat(updatedUnreadComments),
    successState,
  );
};

export const setDevItemCommentsReadReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
